import React, { useCallback } from "react";
import {
  Button,
  Typography,
  Grid,
  Box,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import styles from "./ImageUploadSection.module.css";

const ImageUploadSection = ({
  loading,
  onImageUpload,
  onFileSelected,
  fileInputRef,
  selectedFile,
  description,
  setDescription,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: useCallback(
      (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) onFileSelected(file);
      },
      [onFileSelected]
    ),
    accept: "image/*",
    noClick: true,
  });

  const previewUrl = selectedFile ? URL.createObjectURL(selectedFile) : null;

  return (
    <Grid
      container
      spacing={4}
      sx={{
        minHeight: "100vh",
        p: isMobile ? 2 : 4,
        background:
          "radial-gradient(circle at center, #0F0F1A 0%, #04040A 100%)",
        color: theme.palette.common.white,
      }}
    >
      {/* Left Section - Content */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pr: { md: 6 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              mb: 3,
              fontWeight: 800,
              background: "linear-gradient(45deg, #00C4FF 0%, #8F00FF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.05em",
              fontSize: {
                xs: "2.5rem",
                sm: "3rem",
                md: "3.75rem"
              },
              lineHeight: 1.1,
              textAlign: { xs: "center", md: "left" }
            }}
          >
            Transform Your Face with AI-Powered Wrap Technology
          </Typography>

          <Box
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
              position: "relative",
              border: "1px solid rgba(255,255,255,0.15)",
              mb: 4,
              maxWidth: "100%",
              height: "auto"
            }}
          >
            <img
              src="https://assets.snapedit.app/images/enhance/Step_1.png"
              alt="Face wrap transformation example"
              style={{ 
                width: "100%",
                height: "auto",
                filter: "brightness(0.98) contrast(1.05)"
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: { xs: 12, md: 24 },
                left: { xs: 12, md: 24 },
                bgcolor: "rgba(0,0,0,0.75)",
                px: { xs: 2, md: 3 },
                py: 1,
                borderRadius: "12px",
                backdropFilter: "blur(4px)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.9rem" },
                  letterSpacing: "0.5px",
                }}
              >
                Real-time Face Wrap Preview →
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: "rgba(255,255,255,0.95)",
                textAlign: { xs: "center", md: "left" },
                fontSize: { xs: "1.5rem", md: "1.75rem" }
              }}
            >
              Optimal Image Requirements:
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              {[
                {
                  title: "High Resolution",
                  desc: "Minimum 1024px width, 72+ DPI",
                  icon: "🖼️",
                },
                {
                  title: "Clear Lighting",
                  desc: "Even frontal lighting, no shadows",
                  icon: "💡",
                },
                {
                  title: "Frontal Face",
                  desc: "Direct camera angle, no obstructions",
                  icon: "👤",
                },
                {
                  title: "Neutral Expression",
                  desc: "Eyes open, natural facial features",
                  icon: "😐",
                },
              ].map((item) => (
                <Grid item xs={12} sm={6} key={item.title}>
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: "12px",
                      bgcolor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      height: { xs: "auto", sm: "100px" },
                      minHeight: "90px"
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        color: "#00C4FF",
                        fontSize: { xs: "1.1rem", sm: "1.25rem" }
                      }}
                    >
                      <span>{item.icon}</span>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.75)",
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Grid>

      {/* Right Section - Upload */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            pl: { md: 6 },
          }}
        >
          <div
            {...getRootProps()}
            className={`${styles.uploadSection} ${
              isDragActive ? styles.dragActive : ""
            }`}
          >
            <input {...getInputProps()} />
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                e.target.files[0] && onFileSelected(e.target.files[0])
              }
              ref={fileInputRef}
              style={{ display: "none" }}
            />

            <div className={styles.uploadContent}>
              {previewUrl ? (
                <div className={styles.imageCard}>
                  <div
                    className={`${styles.imageContainer} ${
                      loading ? styles.processing : ""
                    }`}
                  >
                    <img
                      src={previewUrl}
                      alt="Upload preview"
                      className={styles.previewImage}
                    />
                    <div className={styles.analysisOverlay}>
                      <div className={styles.faceGrid} />
                      <div className={styles.landmarkDots} />
                    </div>
                    <div className={styles.shineEffect} />
                  </div>

                  <Box sx={{ mt: 3, width: "100%", px: isMobile ? 1 : 0 }}>
                    <TextField
                      fullWidth
                      label="Wrap Parameters"
                      variant="outlined"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "#fff",
                          "& fieldset": {
                            borderColor: "rgba(255,255,255,0.2)",
                          },
                          "&:hover fieldset": {
                            borderColor: "#00C4FF",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255,255,255,0.6)",
                          fontSize: { xs: "0.9rem", sm: "1rem" }
                        },
                      }}
                      placeholder="Describe desired effects..."
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={onImageUpload}
                      disabled={loading || !description}
                      sx={{
                        mt: 2,
                        py: { xs: 1.25, sm: 1.75 },
                        background:
                          "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
                        color: "white",
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
                        },
                        "&:disabled": {
                          background: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      {loading ? "Applying Face Wrap..." : "Generate 3D Wrap"}
                    </Button>
                  </Box>
                </div>
              ) : (
                <div className={styles.uploadCTA}>
                  <motion.div
                    whileHover={{ scale: isMobile ? 1 : 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="contained"
                      className={styles.uploadButton}
                      onClick={() => fileInputRef.current.click()}
                      disabled={loading}
                      size="large"
                      fullWidth={isMobile}
                      sx={{
                        py: { xs: 2, sm: 3 },
                        px: { xs: 4, sm: 8 },
                        borderRadius: "16px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                        textTransform: "none",
                        border: "2px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      <Typography
                        variant="button"
                        component="span"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          fontSize: { xs: "0.9rem", sm: "inherit" }
                        }}
                      >
                        {isDragActive
                          ? "Drop to Analyze Face ✨"
                          : "Upload Face Image"}
                      </Typography>
                    </Button>
                  </motion.div>

                  <Typography variant="caption" className={styles.uploadHint}>
                    Supported formats: JPEG, PNG, WEBP (Max 20MB)
                  </Typography>

                  <Box sx={{ mt: 4, width: "100%", px: isMobile ? 1 : 0 }}>
                    <TextField
                      fullWidth
                      label="Wrap Customization"
                      variant="outlined"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "#fff",
                          "& fieldset": {
                            borderColor: "rgba(255,255,255,0.2)",
                          },
                          "&:hover fieldset": {
                            borderColor: "#00C4FF",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255,255,255,0.6)",
                          fontSize: { xs: "0.9rem", sm: "1rem" }
                        },
                      }}
                      placeholder="Describe your desired face wrap effect..."
                    />
                  </Box>

                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                      flexWrap: "wrap",
                      px: isMobile ? 1 : 0
                    }}
                  >
                    {[
                      "3D Morphing",
                      "AR Compatible",
                      "Expression Transfer",
                      "Real-time Preview",
                    ].map((tag) => (
                      <Box
                        key={tag}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 0.5, sm: 1 },
                          borderRadius: "8px",
                          bgcolor: "rgba(0,196,255,0.1)",
                          border: "1px solid rgba(0,196,255,0.3)",
                          backdropFilter: "blur(4px)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#00C4FF",
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            letterSpacing: "0.5px",
                          }}
                        >
                          {tag}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </div>
              )}
            </div>
          </div>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ImageUploadSection;